/**
 * Resolves a topic's iconType (or file/urlKey fallback) to an MUI icon component.
 * Used for FAQ topic cards. Uses @mui/icons-material only.
 */
import BoxOutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ApiOutlinedIcon from "@mui/icons-material/ApiOutlined";
import ComputerOutlinedIcon from "@mui/icons-material/ComputerOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import WifiIcon from "@mui/icons-material/Wifi";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

/** Central mapping: iconType string → MUI Icon component */
export const ICON_TYPE_MAP = {
  products: BoxOutlinedIcon,
  settings: SettingsOutlinedIcon,
  integration: ApiOutlinedIcon,
  kiosk: ComputerOutlinedIcon,
  marketing: CampaignOutlinedIcon,
  service: BuildOutlinedIcon,
  loyalty: CreditCardOutlinedIcon,
  tool: BuildOutlinedIcon,
  server: DnsOutlinedIcon,
  default: DescriptionOutlinedIcon,
};

const KEYWORDS = [
  { keys: ["printer", "tlač", "print"], Icon: PrintOutlinedIcon },
  { keys: ["pos", "terminal", "payment", "card"], Icon: CreditCardOutlinedIcon },
  { keys: ["network", "wifi", "internet"], Icon: WifiIcon },
  {
    keys: ["device", "kiosk", "hardware", "setup", "windows"],
    Icon: ComputerOutlinedIcon,
  },
  { keys: ["oberon", "ikelp", "integration"], Icon: ApiOutlinedIcon },
  { keys: ["user", "login", "account"], Icon: PersonOutlinedIcon },
];

/**
 * Resolves icon from topic object (prefers iconType) or from file/urlKey string.
 * @param {object|string} topicOrKey - Topic object { iconType?, file?, urlKey? } or file/urlKey string
 * @returns {React.ComponentType} MUI Icon component
 */
export function getIconForTopic(topicOrKey) {
  const topic =
    topicOrKey && typeof topicOrKey === "object" ? topicOrKey : null;
  const iconType =
    topic && typeof topic.iconType === "string"
      ? topic.iconType.trim().toLowerCase()
      : "";

  if (iconType && ICON_TYPE_MAP[iconType]) {
    return ICON_TYPE_MAP[iconType];
  }
  if (iconType && !ICON_TYPE_MAP[iconType]) {
    return ICON_TYPE_MAP.default;
  }

  const fileNameOrUrlKey =
    topic != null
      ? (topic.file || topic.urlKey || "")
      : typeof topicOrKey === "string"
        ? topicOrKey
        : "";
  const raw = (fileNameOrUrlKey || "")
    .toLowerCase()
    .replace(/\.[a-z0-9]+$/i, "");
  const combined = raw + " " + (fileNameOrUrlKey || "").toLowerCase();

  for (const { keys, Icon } of KEYWORDS) {
    if (keys.some((k) => combined.includes(k))) return Icon;
  }
  return ICON_TYPE_MAP.default;
}
